import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, ArrowLeft, EnvelopeSimple, Lock, PaperPlaneTilt, ArrowClockwise, ShieldPlus, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const REMEMBERED_EMAIL_KEY = "admin_remembered_email";

export default function AdminLogin() {
    const { adminLogin, adminVerify2FA, adminResend2FA, error } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState(() => localStorage.getItem(REMEMBERED_EMAIL_KEY) || "");
    const [password, setPassword] = useState("");
    const [rememberEmail, setRememberEmail] = useState(() => !!localStorage.getItem(REMEMBERED_EMAIL_KEY));
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);

    // 2FA state — populated only when the first login returns two_factor_required.
    const [challenge, setChallenge] = useState(null); // {challengeToken, emailMasked, expiresInMinutes, emailSent}
    const [code, setCode] = useState("");
    const [resendIn, setResendIn] = useState(0);
    const codeInputRef = useRef(null);

    // Countdown for the resend button.
    useEffect(() => {
        if (!resendIn) return;
        const t = setInterval(() => setResendIn((n) => (n > 0 ? n - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, [resendIn]);

    // Autofocus the code input when we transition to step 2.
    useEffect(() => {
        if (challenge) codeInputRef.current?.focus();
    }, [challenge]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        // Persist / clear the remembered email BEFORE the login attempt so the
        // preference sticks whether or not sign-in succeeds.
        if (rememberEmail) {
            localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
        } else {
            localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        }
        const result = await adminLogin(email, password);
        setBusy(false);
        if (result === true) {
            toast.success("Admin authenticated");
            nav("/admin");
            return;
        }
        if (result && result.twoFactorRequired) {
            setChallenge(result);
            setResendIn(result.resendIn || 60);
            toast.success(
                result.emailSent
                    ? `Code sent to ${result.emailMasked}`
                    : `2FA required — but email delivery failed. Check SMTP or contact your system admin.`
            );
            return;
        }
        toast.error(error || "Login failed");
    };

    const verify = async (e) => {
        e.preventDefault();
        setBusy(true);
        const ok = await adminVerify2FA(challenge.challengeToken, code);
        setBusy(false);
        if (ok) { toast.success("Admin authenticated"); nav("/admin"); }
        else { toast.error(error || "Wrong code"); setCode(""); codeInputRef.current?.focus(); }
    };

    const resend = async () => {
        if (resendIn > 0) return;
        setBusy(true);
        const r = await adminResend2FA(challenge.challengeToken);
        setBusy(false);
        if (r.ok) {
            setResendIn(r.resend_in || 60);
            toast.success(r.email_sent ? "A new code is on the way" : "Code refreshed — but email delivery failed. Contact your system admin.");
        } else {
            toast.error(r.detail || "Could not resend");
        }
    };

    const restart = () => {
        setChallenge(null); setCode(""); setResendIn(0); setPassword("");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="relative flex flex-col justify-center px-6 sm:px-12 py-12 max-w-xl mx-auto w-full order-2 lg:order-1 overflow-hidden">
                {/* ─── Decorative background (opaque, non-interactive) ─────── */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0">
                    <div className="absolute inset-0 grid-bg opacity-[0.35]" />
                    {/* Radial glow behind the form */}
                    <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-civic-100 blur-3xl opacity-60" />
                    <div className="absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-emerald-100 blur-3xl opacity-40" />
                    {/* Floating ledger "block" tiles */}
                    <div className="absolute top-10 right-6 rotate-6 opacity-30">
                        <div className="hash-mono text-[10px] tracking-widest text-civic-900/70 border border-civic-300 rounded-md px-2 py-1 bg-white/70 backdrop-blur-sm">BLOCK #001</div>
                    </div>
                    <div className="absolute top-24 right-16 rotate-3 opacity-20">
                        <div className="hash-mono text-[10px] tracking-widest text-civic-900/60 border border-civic-300 rounded-md px-2 py-1 bg-white/60 backdrop-blur-sm">a3f9…ef21</div>
                    </div>
                    <div className="absolute bottom-16 left-4 -rotate-6 opacity-25">
                        <div className="hash-mono text-[9px] tracking-widest text-slate-500 border border-slate-300 rounded-md px-2 py-1 bg-white/70">SHA-256 · immutable</div>
                    </div>
                    {/* Big translucent ballot-tick SVG watermark */}
                    <svg
                        aria-hidden="true"
                        viewBox="0 0 200 200"
                        className="absolute -bottom-8 -right-8 h-64 w-64 text-civic-100/70"
                    >
                        <rect x="20" y="10" width="160" height="180" rx="14" fill="currentColor" />
                        <path
                            d="M65 105 l25 25 l50 -60"
                            fill="none"
                            stroke="rgba(15,82,186,0.35)"
                            strokeWidth="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                <div className="relative z-10">
                <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-slate-500 text-sm mb-8">
                    <ArrowLeft size={16} /> Back
                </Link>
                <Link to="/" className="block mb-8" data-testid="admin-login-logo">
                    <img
                        src="/brand-logo.png"
                        alt="E-VoteVerify.com — Secure · Transparent · Verified · Powered by Blockchain"
                        className="w-4/5 h-auto mx-auto"
                        loading="eager"
                        decoding="async"
                    />
                </Link>
                <div className="overline text-civic-600">Admin console</div>
                <h1 className="font-heading font-black text-4xl mt-2 text-slate-900 tracking-tighter">
                    {challenge ? "Two-step verification" : "Election authority sign-in"}
                </h1>
                <p className="mt-3 text-sm text-slate-600">
                    {challenge
                        ? <>We emailed a 6-digit code to <span className="hash-mono font-semibold text-slate-900" data-testid="twofa-email-masked">{challenge.emailMasked}</span>. It&apos;s valid for {challenge.expiresInMinutes} minutes.</>
                        : "Manage elections, candidates, voter rolls, and verify integrity."}
                </p>

                {!challenge ? (
                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
                        <div>
                            <Label htmlFor="email" className="overline text-slate-500">Email</Label>
                            <div className="relative mt-2">
                                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@evoteverify.com"
                                    className="pl-10 h-11"
                                    required
                                    data-testid="admin-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="pwd" className="overline text-slate-500">Password</Label>
                            <div className="relative mt-2">
                                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <Input
                                    id="pwd"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pl-10 pr-10 h-11"
                                    required
                                    data-testid="admin-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                    data-testid="admin-password-toggle"
                                >
                                    {showPassword
                                        ? <EyeSlash size={16} weight="regular" />
                                        : <Eye size={16} weight="regular" />}
                                </button>
                            </div>
                        </div>
                        <label className="flex items-center gap-2 select-none cursor-pointer text-xs text-slate-600" data-testid="admin-remember-email-label">
                            <input
                                type="checkbox"
                                checked={rememberEmail}
                                onChange={(e) => setRememberEmail(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-civic-600 focus:ring-civic-500 accent-civic-600"
                                data-testid="admin-remember-email"
                            />
                            <span>Remember my email on this device</span>
                        </label>
                        <Button type="submit" disabled={busy} className="w-full h-11 bg-civic-600 hover:bg-civic-700 text-white" data-testid="admin-login-submit">
                            {busy ? "Verifying…" : "Sign in"}
                        </Button>
                    </form>
                ) : (
                    <form onSubmit={verify} className="mt-8 space-y-5" data-testid="admin-2fa-form">
                        <div className="flex items-center gap-2 text-civic-700 bg-civic-50 border border-civic-200 rounded-md p-3">
                            <ShieldPlus size={20} weight="fill" />
                            <div className="text-sm">
                                <div className="font-semibold">Extra security check</div>
                                <div className="text-civic-800/80 text-xs">Codes expire after {challenge.expiresInMinutes} minutes. Max 5 attempts per code.</div>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="twofa-code" className="overline text-slate-500">6-digit code</Label>
                            <Input
                                id="twofa-code"
                                ref={codeInputRef}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                placeholder="123456"
                                className="mt-2 h-14 text-center text-2xl tracking-[0.5em] hash-mono font-bold"
                                required
                                data-testid="admin-2fa-code-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={busy || code.length !== 6}
                            className="w-full h-11 bg-civic-600 hover:bg-civic-700 text-white disabled:bg-slate-300 disabled:text-slate-500"
                            data-testid="admin-2fa-submit"
                        >
                            {busy ? "Verifying…" : "Verify & sign in"}
                        </Button>
                        <div className="flex items-center justify-between text-sm pt-1">
                            <button
                                type="button"
                                onClick={restart}
                                className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 transition-colors"
                                data-testid="admin-2fa-restart"
                            >
                                <ArrowLeft size={14} /> Use a different account
                            </button>
                            <button
                                type="button"
                                onClick={resend}
                                disabled={resendIn > 0 || busy}
                                className="inline-flex items-center gap-1.5 text-civic-600 hover:text-civic-700 font-semibold disabled:text-slate-400 disabled:cursor-not-allowed"
                                data-testid="admin-2fa-resend"
                            >
                                {resendIn > 0
                                    ? <>Resend code in <span className="hash-mono">{resendIn}s</span></>
                                    : <><ArrowClockwise size={14} weight="bold" /> Resend code</>}
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between text-sm">
                    <span className="text-slate-500">Are you a registered voter?</span>
                    <Link to="/login/voter" className="text-civic-600 font-medium underline-offset-4 hover:underline" data-testid="switch-voter-link">
                        Voter login →
                    </Link>
                </div>
                {/* Marketing links — one tap from the admin sign-in to the pillar / features / explorer pages */}
                <div className="mt-6 flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-2 text-xs">
                    <Link to="/learn-more" className="text-civic-600 font-medium underline-offset-4 hover:underline" data-testid="admin-login-learn-more">
                        Learn more →
                    </Link>
                    <Link to="/features" className="text-civic-600 font-medium underline-offset-4 hover:underline" data-testid="admin-login-features">
                        Features
                    </Link>
                    <Link to="/explorer" className="text-slate-500 underline-offset-4 hover:underline hover:text-civic-600" data-testid="admin-login-explorer">
                        Chain explorer
                    </Link>
                </div>
                </div>
            </div>

            <div className="relative hidden lg:flex flex-col justify-between p-12 bg-civic-900 text-white overflow-hidden order-1 lg:order-2">
                {/* ─── Layered opaque decoration ────────────────────────────── */}
                <div aria-hidden="true" className="absolute inset-0 grid-bg opacity-20" />
                {/* Two large blurred gradient orbs for depth */}
                <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-civic-500/25 blur-3xl" />
                <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-emerald-500/10 blur-3xl" />
                {/* Chained "blocks" in the negative space — evokes the ledger */}
                <div aria-hidden="true" className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 opacity-40 hidden xl:block">
                    <div className="flex flex-col gap-3">
                        {["a3f9…ef21", "9c02…4d18", "77e1…ba03", "b6da…0917", "31f4…c22e"].map((h, i) => (
                            <div
                                key={h}
                                className="flex items-center gap-2 pl-3 pr-4 py-2 rounded-md border border-white/10 bg-white/[0.04] backdrop-blur-sm w-56"
                                style={{ transform: `translateX(${i % 2 ? "12px" : "0"})` }}
                            >
                                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                                <span className="hash-mono text-[10px] text-civic-100/80">#{String(1024 + i)}</span>
                                <span className="hash-mono text-[10px] text-white/60 ml-auto">{h}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Faint diagonal SHA line */}
                <div aria-hidden="true" className="pointer-events-none absolute bottom-24 left-0 right-0 h-px bg-gradient-to-r from-transparent via-civic-200/40 to-transparent" />
                {/* Corner shield watermark */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 200 200"
                    className="pointer-events-none absolute -top-8 -left-8 h-56 w-56 text-white/[0.04]"
                >
                    <path d="M100 15 L175 45 L175 105 C175 145 140 175 100 190 C60 175 25 145 25 105 L25 45 Z" fill="currentColor" />
                </svg>

                <div className="relative">
                    {challenge
                        ? <PaperPlaneTilt size={48} weight="duotone" className="text-civic-200 mb-6" />
                        : <ShieldCheck size={48} weight="duotone" className="text-civic-200 mb-6" />}
                    <h2 className="font-heading text-4xl font-black tracking-tighter leading-none">
                        {challenge ? <>Check your <br />inbox.</> : <>Custody of <br />the ledger.</>}
                    </h2>
                    <p className="mt-5 text-civic-100 text-sm max-w-sm leading-relaxed">
                        {challenge
                            ? <>Two-step verification protects every admin account except the seeded System Administrator. Your one-time code was emailed to the address on your admin profile.</>
                            : "Election officials draft elections, register voters, activate ballots and supervise public audit. Every action is logged."}
                    </p>

                    {/* Stat pills — visible depth, still opaque graphics */}
                    {!challenge && (
                        <div className="mt-8 flex flex-wrap gap-2 max-w-sm">
                            <div className="rounded-md border border-white/10 bg-white/[0.05] backdrop-blur-sm px-3 py-2">
                                <div className="overline text-civic-200/80 text-[9px]">Hash</div>
                                <div className="hash-mono text-[11px] text-white/90 mt-0.5">SHA-256</div>
                            </div>
                            <div className="rounded-md border border-white/10 bg-white/[0.05] backdrop-blur-sm px-3 py-2">
                                <div className="overline text-civic-200/80 text-[9px]">Auth</div>
                                <div className="hash-mono text-[11px] text-white/90 mt-0.5">JWT · 2FA</div>
                            </div>
                            <div className="rounded-md border border-white/10 bg-white/[0.05] backdrop-blur-sm px-3 py-2">
                                <div className="overline text-civic-200/80 text-[9px]">Audit</div>
                                <div className="hash-mono text-[11px] text-white/90 mt-0.5">Immutable</div>
                            </div>
                        </div>
                    )}
                </div>
                <Link to="/" className="relative inline-flex items-center gap-2 text-white/80 hover:text-white text-sm">
                    <ArrowLeft size={16} /> Back to Home
                </Link>
            </div>
        </div>
    );
}
